import api, { jwksApi } from 'shared/services/api-communicator';
import { responseFormatter } from 'utils';
import config from 'config';
import { noop } from 'shared/constants';

const RC_PRODUCTION_URL = config.rcApiBaseUrl.includes('devtest')
  ? 'https://platform.ringcentral.com/restapi/v1.0'
  : config.rcApiBaseUrl;

const getVersion = async () => {
  return responseFormatter(
    api.get('/version', {
      setAuthHeader: true,
    })
  );
};
const getAccountName = async uid => {
  return responseFormatter(
    api.get(`sfdc/account/${uid}`, {
      setAuthHeader: true,
    })
  );
};

const createEmeregencyLocations = async (
  accountUid = '',
  token = '',
  payload = {},
  rateLimitReachedCallback = noop
) => {
  if (payload.site.id === 'main-site') {
    delete payload.site
  }

  return new Promise(async (resolve, reject) => {
    const { data, error } = await responseFormatter.rc(
      jwksApi.post(
        `${RC_PRODUCTION_URL}/account/${accountUid}/emergency-locations`,
        payload,
        {
          hasToken: token,
          accountUid,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      resolve({
        data,
        error: null,
      });
    }
  });
};

const getAllExtensions = async (accountUid, token, query, rateLimitReachedCallback = noop) => {
  return new Promise(async (resolve, reject) => {
    const extensions = [];
    let pageCounter = 1;
    const { data, error, paging } = await responseFormatter.rc(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/extension?perPage=1000${
          query ? `&${query}` : ''
        }`,
        {
          hasToken: token,
          accountUid,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      extensions.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await responseFormatter.rc(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/extension?page=${pageCounter}&perPage=1000${
                query ? `&${query}` : ''
              }`,
              {
                hasToken: token,
                accountUid,
                hooks: { rateLimitReached: rateLimitReachedCallback },
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            extensions.push(...data);
          }
        }
        resolve({
          data: extensions,
          error: null,
        });
      } else {
        resolve({
          data: extensions,
          error: null,
        });
      }
    }
  });
};

const getSites = async (accountUid, token, query, rateLimitReachedCallback = noop) => {
  return new Promise(async (resolve, reject) => {
    const sites = [];
    let pageCounter = 1;
    const { data, error, paging } = await responseFormatter.rc(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/account/${accountUid}/sites?perPage=1000${
          query ? `&${query}` : ''
        }`,
        {
          hasToken: token,
          accountUid,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      sites.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await responseFormatter.rc(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/account/${accountUid}/sites?page=${pageCounter}&perPage=1000${
                query ? `&${query}` : ''
              }`,
              {
                hasToken: token,
                accountUid,
                hooks: { rateLimitReached: rateLimitReachedCallback },
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            sites.push(...data);
          }
        }
        resolve({
          data: sites,
          error: null,
        });
      } else {
        resolve({
          data: sites,
          error: null,
        });
      }
    }
  });
};

const getCountryList = async (token, query, rateLimitReachedCallback = noop) => {
  return new Promise(async (resolve, reject) => {
    const countryList = [];
    let pageCounter = 1;
    const { data, error, paging } = await responseFormatter.rc(
      jwksApi.get(
        `${RC_PRODUCTION_URL}/dictionary/country?perPage=1000${
          query ? `&${query}` : ''
        }`,
        {
          hasToken: token,
          hooks: { rateLimitReached: rateLimitReachedCallback },
          headers: {
            Authorization: `Bearer ${token && token.accessToken}`,
          },
        }
      )
    );
    if (error) {
      resolve({
        data: null,
        error,
      });
    } else {
      countryList.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await responseFormatter.rc(
            jwksApi.get(
              `${RC_PRODUCTION_URL}/dictionary/country?page=${pageCounter}&perPage=1000${
                query ? `&${query}` : ''
              }`,
              {
                hasToken: token,
                hooks: { rateLimitReached: rateLimitReachedCallback },
                headers: {
                  Authorization: `Bearer ${token && token.accessToken}`,
                },
              }
            )
          );
          if (error) {
            resolve({
              data: null,
              error,
            });
          } else {
            countryList.push(...data);
          }
        }
        resolve({
          data: countryList,
          error: null,
        });
      } else {
        resolve({
          data: countryList,
          error: null,
        });
      }
    }
  });
};

export default {
  getAccountName,
  createEmeregencyLocations,
  getVersion,
  getAllExtensions,
  getCountryList,
  getSites
};
